import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface LetterheadData {
  date: string;
  recipientName: string;
  letterContent: string;
  senderName: string;
  roleDesignation: string;
  signatureImage?: string; // Base64 encoded image
}

// Company Details (matching the template)
const COMPANY_DETAILS = {
  name: 'BOZ Meet Connect and Travel Pvt Ltd',
  address: 'Hustlehub Tech Park, 36/5, Somasundarapalya Main Rd, HSR Layout, Bengaluru, Karnataka 560102',
  phone: '9591592804',
  email: 'support@bozapp.in',
  website: 'www.bozapp.in'
};

async function loadLogoAsBase64(): Promise<string | null> {
  try {
    const response = await fetch('/logo.png');
    if (!response.ok) {
      throw new Error('Logo not found');
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.log('Could not load logo:', error);
    return null;
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export async function generateLetterheadPDF(data: LetterheadData): Promise<void> {
  // Escape user input to prevent XSS
  const safeDate = escapeHtml(data.date);
  const safeRecipientName = escapeHtml(data.recipientName);
  const safeLetterContent = escapeHtml(data.letterContent).replace(/\n/g, '<br>');
  const safeSenderName = escapeHtml(data.senderName);
  const safeRoleDesignation = escapeHtml(data.roleDesignation);
  
  // Load logo as base64 first
  const logoBase64 = await loadLogoAsBase64();
  const logoImgTag = logoBase64 
    ? `<img src="${logoBase64}" alt="BOZ logo" />`
    : '<div style="width: 88px; height: 88px; background: linear-gradient(180deg, #fff, #f3f7ff); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 24px; color: #0b61b7;">BOZ</div>';
  
  // Create a temporary container for the letterhead HTML
  const tempContainer = document.createElement('div');
  tempContainer.style.position = 'absolute';
  tempContainer.style.left = '-9999px';
  tempContainer.style.width = '210mm'; // A4 width
  tempContainer.style.background = '#ffffff';
  tempContainer.innerHTML = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="utf-8">
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700&display=swap" rel="stylesheet">
      <style>
        :root {
          --brand-blue: #0b61b7;
          --brand-dark: #073f78;
          --muted: #6b7280;
          --page-bg: #ffffff;
          --gutter: 28mm;
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
          color: #111827;
          background: var(--page-bg);
        }
        
        .page {
          width: 210mm;
          min-height: 297mm;
          background: var(--page-bg);
          position: relative;
          padding: var(--gutter);
        }
        
        header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
        }
        
        .brand {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        
        .logo {
          width: 88px;
          height: 88px;
          background: linear-gradient(180deg, #fff, #f3f7ff);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border: 1px solid rgba(11, 97, 183, 0.08);
        }
        
        .logo img {
          max-width: 100%;
          max-height: 100%;
          display: block;
          object-fit: contain;
        }
        
        .brand-info {
          line-height: 1;
        }
        
        .brand-info .name {
          font-weight: 700;
          font-size: 20px;
          color: var(--brand-dark);
        }
        
        .brand-info .tag {
          font-size: 12px;
          color: var(--muted);
          margin-top: 4px;
        }
        
        .contact {
          text-align: right;
          font-size: 12px;
          color: var(--muted);
        }
        
        .contact div {
          margin-bottom: 4px;
        }
        
        .contact .website {
          color: var(--brand-blue);
          font-weight: 600;
        }
        
        .accent {
          height: 6px;
          background: linear-gradient(90deg, var(--brand-blue), var(--brand-dark));
          border-radius: 4px;
          margin: 14px 0 20px 0;
        }
        
        .content {
          min-height: 160mm;
          font-size: 14px;
          color: #111827;
        }
        
        .content p {
          margin: 0 0 12px 0;
        }
        
        footer {
          position: absolute;
          left: var(--gutter);
          right: var(--gutter);
          bottom: 18mm;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: var(--muted);
        }
        
        .footer-left {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        
        .divider {
          width: 1px;
          height: 18px;
          background: #e6eefc;
        }
        
        .signature {
          margin-top: 34px;
          display: flex;
          gap: 28px;
          align-items: flex-start;
        }
        
        .sig-box {
          min-width: 220px;
        }
        
        .sig-image {
          max-width: 180px;
          max-height: 80px;
          margin-bottom: 12px;
          object-fit: contain;
        }
        
        .sig-line {
          border-top: 1px dashed #dbeafe;
          margin-top: 28px;
        }
        
        /* Split letter content into paragraphs */
        .letter-content {
          white-space: pre-wrap;
          word-wrap: break-word;
        }
      </style>
    </head>
    <body>
      <div class="page">
        <header>
          <div class="brand">
            <div class="logo">
              ${logoImgTag}
            </div>
            <div class="brand-info">
              <div class="name">${COMPANY_DETAILS.name}</div>
              <div class="tag">${COMPANY_DETAILS.address}</div>
            </div>
          </div>
          <div class="contact">
            <div><strong>Phone:</strong> ${COMPANY_DETAILS.phone}</div>
            <div><strong>Email:</strong> ${COMPANY_DETAILS.email}</div>
            <div class="website">${COMPANY_DETAILS.website}</div>
          </div>
        </header>
        
        <div class="accent"></div>
        
        <main class="content">
          <p style="color: var(--muted);">Date: ${safeDate}</p>
          
          <p>Dear <strong>${safeRecipientName}</strong>,</p>
          
          <div class="letter-content">${safeLetterContent}</div>
          
          <p>Sincerely,</p>
          
          <div class="signature">
            <div class="sig-box">
              ${data.signatureImage ? `<img src="${data.signatureImage}" alt="Signature" class="sig-image" />` : ''}
              <div style="font-weight: 600;">${safeSenderName}</div>
              <div style="font-size: 12px; color: var(--muted);">${safeRoleDesignation}</div>
              <div class="sig-line"></div>
            </div>
          </div>
        </main>
        
        <footer>
          <div class="footer-left">
            <div style="font-weight: 600; color: var(--brand-dark);">${COMPANY_DETAILS.name}</div>
            <div class="divider"></div>
            <div>Hustlehub Tech Park, 36/5, Somasundarapalya Main Rd, HSR Layout, Bengaluru</div>
          </div>
          <div style="text-align: right">
            <div><strong>Phone:</strong> ${COMPANY_DETAILS.phone}</div>
            <div><strong>Email:</strong> ${COMPANY_DETAILS.email}</div>
            <div style="margin-top: 6px; font-weight: 600; color: var(--brand-blue);">${COMPANY_DETAILS.website}</div>
          </div>
        </footer>
      </div>
    </body>
    </html>
  `;

  // Append to body temporarily
  document.body.appendChild(tempContainer);

  try {
    // Wait for fonts and images to load
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Get the page element
    const pageElement = tempContainer.querySelector('.page') as HTMLElement;
    if (!pageElement) {
      throw new Error('Could not find page element');
    }

    // Convert to canvas using html2canvas
    // Using scale 1.5 for good quality while keeping file size reasonable
    // For PDFs, 1.5x scale provides excellent quality (300 DPI equivalent)
    const canvas = await html2canvas(pageElement, {
      scale: 1.5,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      width: pageElement.scrollWidth || 794, // A4 width in pixels at 96 DPI
      height: pageElement.scrollHeight || 1123, // A4 height in pixels at 96 DPI
    });

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true, // Enable PDF compression
    });

    // Calculate dimensions
    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Convert canvas to JPEG with quality 0.85 for good balance between quality and file size
    // JPEG is much smaller than PNG for documents with text and images
    const imgData = canvas.toDataURL('image/jpeg', 0.85);
    pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');

    // Download PDF
    const fileName = `BOZ_Letterhead_${data.date.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
    pdf.save(fileName);
  } finally {
    // Clean up
    document.body.removeChild(tempContainer);
  }
}

